import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { hotComics, latestUpdates, completedComics } from '@/mocks/comics';
import { WebSiteSchema, WebPageSchema, BreadcrumbListSchema, ArticleSchema } from '@/components/base/SchemaOrg';

const allComics = [...hotComics, ...latestUpdates, ...completedComics];

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const timer = setTimeout(() => setIsLoading(false), 300);
    return () => clearTimeout(timer);
  }, [id]);

  const comic = allComics.find((c) => c.id === id);

  if (isLoading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="flex items-center gap-3 text-foreground-400">
            <div className="w-6 h-6 border-2 border-primary-400 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-sm">加载中...</span>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center px-4">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-300 mx-auto mb-4">
              <i className="ri-error-warning-line text-2xl"></i>
            </div>
            <h2 className="text-xl font-heading font-semibold text-foreground-800 mb-2">漫画未找到</h2>
            <p className="text-sm text-foreground-500 mb-6">该漫画不存在或已被下架</p>
            <Link
              to="/category"
              className="inline-flex items-center gap-2 px-5 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-full transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              返回漫画列表
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <WebSiteSchema />
      <WebPageSchema
        title={`${comic.title} - 奇妙漫画免费全集在线看`}
        description={comic.description}
        url={`https://www.qimiaomanhua.com/comic/${comic.id}`}
      />
      <BreadcrumbListSchema
        items={[
          { name: '首页', url: 'https://www.qimiaomanhua.com' },
          { name: '漫画分类', url: 'https://www.qimiaomanhua.com/category' },
          { name: comic.categories[0], url: `https://www.qimiaomanhua.com/category?type=${comic.categories[0] === '韩漫' ? 'korean' : comic.categories[0] === '日漫' ? 'japanese' : comic.categories[0] === '国漫' ? 'chinese' : 'all'}` },
          { name: comic.title, url: `https://www.qimiaomanhua.com/comic/${comic.id}` },
        ]}
      />
      <ArticleSchema
        article={{
          title: comic.title,
          description: comic.description,
          author: comic.author,
          datePublished: comic.updatedAt,
          image: comic.coverImage,
          url: `https://www.qimiaomanhua.com/comic/${comic.id}`,
        }}
      />
      <Navbar />
      <main className="pt-16">
        {/* Breadcrumb */}
        <section className="w-full px-4 md:px-6 py-4 bg-background-50 border-b border-background-200">
          <div className="max-w-7xl mx-auto">
            <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-400" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to="/category" className="hover:text-primary-500 transition-colors">漫画分类</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link
                to={`/category?type=${comic.categories[0] === '韩漫' ? 'korean' : comic.categories[0] === '日漫' ? 'japanese' : comic.categories[0] === '国漫' ? 'chinese' : 'all'}`}
                className="hover:text-primary-500 transition-colors"
              >
                {comic.categories[0]}
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-700 font-medium">{comic.title}</span>
            </nav>
          </div>
        </section>

        {/* Comic Header */}
        <section className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row gap-8">
              {/* Cover */}
              <div className="flex-shrink-0 w-48 md:w-56 mx-auto md:mx-0">
                <div className="aspect-[3/4] rounded-xl overflow-hidden border border-background-200">
                  <img
                    src={comic.coverImage}
                    alt={comic.title}
                    title={`${comic.title} - 奇妙漫画全集免费阅读`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                  {comic.title}
                </h1>
                <p className="text-foreground-500 text-sm md:text-base leading-relaxed mb-5">
                  {comic.description}
                </p>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                  {[
                    { label: '作者', value: comic.author, icon: 'ri-user-line' },
                    { label: '状态', value: comic.status, icon: 'ri-bookmark-line' },
                    { label: '评分', value: comic.rating, icon: 'ri-star-line' },
                    { label: '章节', value: `共${comic.chapters.length}话`, icon: 'ri-file-list-2-line' },
                    { label: '人气', value: `${(comic.views / 10000).toFixed(0)}万`, icon: 'ri-eye-line' },
                    { label: '收藏', value: `${(comic.followers / 10000).toFixed(1)}万`, icon: 'ri-heart-line' },
                    { label: '更新', value: comic.updatedAt, icon: 'ri-calendar-line' },
                    { label: '类型', value: comic.categories.join(' · '), icon: 'ri-price-tag-3-line' },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center gap-2 p-2.5 rounded-lg bg-background-100">
                      <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg bg-background-200">
                        <i className={`${item.icon} text-sm text-foreground-500`}></i>
                      </div>
                      <div className="min-w-0">
                        <p className="text-[10px] text-foreground-400">{item.label}</p>
                        <p className="text-sm font-medium text-foreground-800 truncate">{item.value}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {comic.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button className="flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold text-sm rounded-full transition-colors whitespace-nowrap">
                    <i className="ri-play-circle-line text-lg"></i>
                    开始阅读 第1话
                  </button>
                  <button className="flex items-center justify-center gap-2 px-6 py-3 bg-background-100 hover:bg-background-200 text-foreground-700 font-semibold text-sm rounded-full border border-background-200 transition-colors whitespace-nowrap">
                    <i className="ri-heart-line text-lg"></i>
                    加入收藏
                  </button>
                  <button className="flex items-center justify-center gap-2 px-6 py-3 bg-background-100 hover:bg-background-200 text-foreground-700 font-semibold text-sm rounded-full border border-background-200 transition-colors whitespace-nowrap">
                    <i className="ri-share-forward-line text-lg"></i>
                    分享
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Chapter List */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                章节列表
              </h2>
              <div className="flex items-center gap-2">
                <button className="px-3 py-1.5 text-xs font-medium bg-primary-500 text-white rounded-full whitespace-nowrap">
                  正序
                </button>
                <button className="px-3 py-1.5 text-xs font-medium bg-background-200 text-foreground-500 rounded-full whitespace-nowrap hover:bg-background-300 transition-colors">
                  倒序
                </button>
              </div>
            </div>

            {/* Chapter Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
              {comic.chapters.map((chapter) => (
                <button
                  key={chapter.id}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 hover:bg-primary-50/50 transition-all duration-200 text-left"
                >
                  <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 font-medium text-xs">
                    {chapter.index}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground-800 truncate">
                      {chapter.title}
                    </p>
                    <p className="text-[10px] text-foreground-400">{chapter.updatedAt}</p>
                  </div>
                </button>
              ))}
            </div>

            {comic.chapters.length > 30 && (
              <div className="text-center mt-8">
                <button className="px-6 py-2.5 bg-background-200 hover:bg-background-300 text-foreground-600 text-sm font-medium rounded-full transition-colors whitespace-nowrap">
                  展开全部章节
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Reading Experience */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
              阅读体验
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {[
                {
                  icon: 'ri-scroll-to-bottom-line',
                  title: '下拉式连续阅读',
                  desc: '向下滑动即可连续阅读，无需翻页，沉浸式漫画体验，专为手机端优化设计。',
                },
                {
                  icon: 'ri-smartphone-line',
                  title: '多端适配',
                  desc: '完美适配手机、平板和桌面端，随时随地畅享漫画阅读的乐趣。',
                },
                {
                  icon: 'ri-bookmark-3-line',
                  title: '智能书签',
                  desc: '自动记录阅读进度，下次打开继续阅读，不错过每一话的精彩内容。',
                },
              ].map((feature) => (
                <div key={feature.title} className="p-5 rounded-xl bg-background-100 border border-background-200 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                    <i className={`${feature.icon} text-xl`}></i>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-900 mb-2">{feature.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Comics */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
              相关推荐
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {allComics
                .filter((c) => c.id !== comic.id && c.categories.some((cat) => comic.categories.includes(cat)))
                .slice(0, 6)
                .map((related) => (
                  <Link
                    key={related.id}
                    to={`/comic/${related.id}`}
                    className="group bg-background-50 rounded-xl overflow-hidden border border-background-200 hover:border-primary-300 transition-all duration-200 hover:-translate-y-1"
                  >
                    <div className="aspect-[3/4] overflow-hidden">
                      <img
                        src={related.coverImage}
                        alt={related.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-3">
                      <p className="font-heading font-semibold text-foreground-900 text-sm line-clamp-1">{related.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="flex items-center gap-0.5 text-xs text-amber-500">
                          <i className="ri-star-fill text-[10px]"></i>
                          {related.rating}
                        </span>
                        <span className="text-[10px] text-foreground-400">{related.status}</span>
                      </div>
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}